<script lang="ts" setup>
import { reactive, computed, watch, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useEvents } from '@/plugins/events';
import { Form } from '@/helpers/Form';
import Pager from '@/helpers/Pager';
import ProcessLogDetailsModal from '../modals/ProcessLogDetailsModal.vue';

const props = defineProps({
  "publicId": null
});

const { $events } = useEvents();
const store = useStore();

const pager = reactive(new Pager(1, 10, 'DateCreatedUtc', 'DESC')) as Pager;
const filter = reactive(Form.create<any>({}));
const filterBuilder = reactive<Record<string, unknown>>({});

const licence = computed(() => `process-logs`);
const parentType = computed(() => `scripts`);
const endpoint = computed(() => `${parentType.value}/${props.publicId}/${licence.value}`);
const fileName = computed(() => `${licence.value}_${props.publicId}`);
const filtersDone = computed(() => store.state.common.grid.filtersInitialized);

const processLogDetailsModal = ref<InstanceType<typeof ProcessLogDetailsModal> | null>(null);

const onRowClick = async (item: any) =>
{
    if (processLogDetailsModal.value)
    {
        await processLogDetailsModal.value.openWithLogId(item.result.id);
    }
};

const overwriteFilter = (event: { key: string; value: any; period: boolean; amount: boolean }) =>
{
    if (event.period)
    {
        filterBuilder[`${event.key}.FromUtc`] = event.value;
        filterBuilder[`${event.key}.ToUtc`] = event.value;
        filterBuilder[`${event.key}.DaysBeforeNow`] = event.value;
        filterBuilder[`${event.key}.DaysAfterNow`] = event.value;
    }
    else if (event.amount)
    {
        filterBuilder[`${event.key}.FromUtc`] = event.value;
        filterBuilder[`${event.key}.ToUtc`] = event.value;
    }
    else filterBuilder[event.key] = event.value;
};

const initializationChanged = (newValue: boolean) =>
{
    if (newValue)
    {
        filter.setInitialValues(filterBuilder);
        filter.withData(filterBuilder);
    }
};

watch(() => filtersDone.value, initializationChanged);

onUnmounted(() =>
{
    // ALWAYS CLEAR FILTER INITIALIZATION WHEN LEAVING A COMPONENT IN MODULE SCOPE
    store.commit('common/grid/SET_INITIALIZATION', false);
});
</script>

<template>
    <DynamicGrid :pager="pager" :filter="filter" :endpoint="endpoint" :view-state-key="endpoint" :row-click="onRowClick">
        <!-- Header -->
        <template #header>
            <IdeoPanel :column="false" :stretch="false">
                <template #default>
                    <NewFiltersWrapper :filter="filter" :url="licence" :pager="pager" :view-state-key="endpoint">
                        <DynamicFilters
                            :request-url="licence"
                            :filter="filter"
                            :parent-type="parentType"
                            :parent-id="publicId"
                            :view-state-key="endpoint"
                            @filter-update="overwriteFilter"
                        />
                    </NewFiltersWrapper>
                </template>

                <template #end>
                    <div class="toolbar-actions">
                        <FilterManager :endpoint="endpoint" />
                        <ColumnManager :endpoint="endpoint" />
                        <ExportManager
                            :excel="{ endpoint }"
                            :filter="filter"
                            :pager="pager"
                        />
                    </div>
                </template>
            </IdeoPanel>
        </template>
    </DynamicGrid>

    <!-- Modal -->
    <ProcessLogDetailsModal ref="processLogDetailsModal" />
</template>
